import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@3plearning/react-components';
import {
  SearchableListWrapper,
  InputWrapper,
  SelectPanelWrapper,
  showElement,
  SearchContainer,
  FilterWrapper,
  FilteredListWrapper
} from './styled';
import {
  strandExpandCollapse,
  updateStrand,
  applyFilter
} from '../../containers/upf-assign/upf-assign-actions';
import SvgIcon from '../svg-icon/svg-icon';
import { FilterIcon, FilterOutlineIcon, DownArrow, UpArrow } from '../../helpers/icon-dictionary';
import SelectableListItem from '../selectable-list-item';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import {
  CHECKED,
  UNCHECKED,
  INDETERMINATE
} from '../checkbox/constants';
import ErrorBoundary from '../../containers/error-boundary';

const SearchableDropDownList = (props) => {
  const dispatch = useDispatch();
  const { i18n } = props;

  const topics = useSelector(state => state.UPFAssignReducer.topics);
  const selectedStandardCount = useSelector(state => state.UPFAssignReducer.selectedStandardCount);
  const standardCount = useSelector(state => state.UPFAssignReducer.standardCount);

  const [searchTerm, setSearchTerm] = useState('');
  const searchTextbox = useRef(null);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const label = i18n.t`Filter by Standard - showing ${selectedStandardCount} of ${standardCount} standards`;

  const handleClick = () => {
    if (topics.length) {
      setShowSearchPanel(true);
      searchTextbox.current.placeholder = i18n.t`Type here to search for Standards`;
    }
  };

  const updateStrands = (actionType, id, checked) => {
    dispatch(updateStrand(actionType, id, checked, searchTerm));
  };

  const handleOnChange = (e) => {
    const searchText = e.target.value;
    setSearchTerm(searchText);
    updateStrands('onSearch', '', false, searchText);
    if (!searchText) {
      searchTextbox.current.placeholder = i18n.t`Type here to search for Standards`;
    }
  };

  const uncheckAll = () => {
    updateStrands('uncheckAll');
  };

  const apply = () => {
    setShowSearchPanel(false);
    dispatch(applyFilter());
    searchTextbox.current.placeholder = label;
  };

  const onSelect = (id, checked) => {
    updateStrands('selection', id, checked, searchTerm);
  };

  const getChildTopics = item => item.childTopics.map(std => (
    <div className="spacer" key={`${item.id}#${std.id}`}>
      <SelectableListItem
        id={`${item.id}#${std.id}`}
        checked={std.selected ? CHECKED : UNCHECKED}
        name={std.displayName}
        onChange={onSelect} />
    </div>)
  );

  const onStrandIconClick = (id) => {
    dispatch(strandExpandCollapse(id));
  };

  const getCheckedState = (item) => {
    const selectedChildLength = item.childTopics.filter(childTopic => childTopic.selected).length;

    if (!selectedChildLength) return UNCHECKED;

    if (item.childTopics.length === selectedChildLength) return CHECKED;

    return INDETERMINATE;
  };

  const renderStrandItems = () => {
    if (topics) {
      return topics.map(item =>
        (<React.Fragment key={item.id}>
          <div className="strand">
            <div className="wrapper">
              <div className="Description">
                <SelectableListItem
                  id={item.id}
                  checked={getCheckedState(item)}
                  name={item.displayName}
                  onChange={onSelect} />
              </div>
              <div className="icon" tabIndex={0} onClick={() => onStrandIconClick(item.id)} role="button">
                <SvgIcon icon={item.isExpand ? UpArrow : DownArrow} /></div>
            </div>
            {item.isExpand ? getChildTopics(item) : null}
          </div><hr className="line" />
        </React.Fragment>)
      );
    }

    return null;
  };

  const focusSearchTextbox = () => {
    searchTextbox.current.placeholder = label;
  };

  const logoTitle = selectedStandardCount === standardCount ?
    i18n.t`No filters have been applied - entire curriculum grade is shown`
    : i18n.t`Filters have been applied - click search bar to see which standards have been excluded`;

  return (
    <OutsideClickHandler
      callback={() => showSearchPanel && setShowSearchPanel(false)}
      forButton="SearchableDropDownList">
      <ErrorBoundary>
        <div>
          <OutsideClickHandler
            callback={focusSearchTextbox}
            forButton="searchPanel">
            <div id="searchPanel">
              <SearchContainer>
                <FilterWrapper>
                  <label htmlFor="filter">{i18n.t`Filter`}</label>
                  <SearchableListWrapper>
                    <div className="searchText">
                      <InputWrapper
                        ref={searchTextbox}
                        type="text"
                        onClick={handleClick}
                        onChange={handleOnChange}
                        value={searchTerm}
                        placeholder={label}
                        data-name="filter" />
                    </div>
                    <div className="searchLogo" title={logoTitle}>
                      {selectedStandardCount === standardCount ? (<SvgIcon icon={FilterOutlineIcon} />)
                        : (<FilteredListWrapper><SvgIcon icon={FilterIcon} /></FilteredListWrapper>)}
                    </div>
                  </SearchableListWrapper>
                </FilterWrapper>
              </SearchContainer>
              <SelectPanelWrapper
                style={showElement(showSearchPanel)}
              >
                <div>
                  <div className="panelBody">{renderStrandItems()}</div>
                  <div className="panelFooter">
                    <div className="uncheck"><Button
                      className="unCheckAllButton"
                      disabled={!topics.length}
                      text={i18n.t`Unselect all`}
                      type={Button.LOZENGE}
                      onClick={uncheckAll}
                    /></div>
                    <div className="apply"><Button
                      className="applyButton"
                      disabled={!topics.length}
                      primary
                      text={i18n.t`Apply filter`}
                      type={Button.LOZENGE}
                      onClick={apply} /></div>
                  </div>
                </div>
              </SelectPanelWrapper>
            </div>
          </OutsideClickHandler>
        </div>
      </ErrorBoundary>
    </OutsideClickHandler>
  );
};

SearchableDropDownList.propTypes = {
  i18n: PropTypes.object
};

export default SearchableDropDownList;



// WEBPACK FOOTER //
// ./components/searchable-dropdownlist/index.js